<script>
import { untrack } from "svelte";
import { Group, Image, Rect, Text } from "svelte-konva";
import convertToPlainText from "@cclib/functions/quill/convertToPlainText.js";

import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
import { editorState } from "@app/store/editorStore.svelte.js";
import { markerState } from "@app/store/markerStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import { viewportState } from "@app/lib/timeline/viewport.svelte.js";
import { upperBound, lowerBound } from "@app/lib/timeline/regionGeometry.js";
import { BADGES, BADGE_SIZE, BADGE_GAP, badgesForEvent } from "@app/lib/timeline/icons.js";
import {
    gatherRegionEdgeTimes,
    clampUniformDelta,
    computeHandleBounds,
    computePermissiveHandleBounds,
    resolveHandleTime,
    nearestSnapTarget,
    findRippleTargetIndex,
    applyResizeRipple,
    applyMoveRipple
} from "@app/lib/timeline/editing.js";
import { playerState } from "@app/store/playerStore.svelte.js";
import { frameGapToSeconds } from "@app/lib/timeline/snapping.js";
import { renderWaveformTile } from "@app/lib/timeline/waveformImage.js";
import { ensureEventPeaks } from "@app/lib/audio/assignEventAudio.js";
import { themeState } from "@app/store/themeStore.svelte.js";
import { getTimelinePalette } from "@app/lib/timeline/timelinePalette.js";

/**
 * Renders one event-group track as a row of region rectangles. Performance
 * relies on:
 *   - sorted-by-start cache rebuilt only when the group changes;
 *   - binary-search culling against the visible viewport plus a buffer wide
 *     enough to cover the longest event (so regions whose start is offscreen
 *     but whose end overlaps the viewport still render);
 *   - per-region drag/resize using Konva `draggable` + `dragBoundFunc` for hard
 *     constraints (no overlap, frame gap, snapping). All store mutation
 *     happens on `dragend` so undo/redo records one action per drag.
 *
 * Resize uses two thin per-region handle Rects (rendered only when selected)
 * rather than a shared Konva.Transformer; Transformer applies `scaleX` to a
 * Group which would visually stretch the region text and badges.
 *
 * @typedef {Object} Props
 * @property {number} groupIndex
 * @property {boolean} [isPrimary]
 * @property {number} y
 * @property {number} height
 * @property {(eventIndex: number, evt: PointerEvent) => void} [onRegionClick]
 * @property {(eventIndex: number, evt: PointerEvent) => void} [onRegionDoubleClick]
 * @property {() => void} [onDragEndCommit]
 * @property {(timeSec: number, evt: PointerEvent) => void} [onBackgroundPointerDown]
 */

/** @type {Props} */
let {
    groupIndex,
    isPrimary = false,
    y = 0,
    height = 60,
    onRegionClick,
    onRegionDoubleClick,
    onDragEndCommit,
    onBackgroundPointerDown
} = $props();

// [isSelected][isError]
const PRIMARY_COLOR = {
    true:  { true: "#ff8f00b0", false: "#bc832480" },
    false: { true: "#e74c3c80", false: "#0d6efd33" }
};
const SECONDARY_COLOR = "#909da980";
const SECONDARY_STROKE = "rgba(214, 226, 240, 0.65)";
const SECONDARY_STROKE_WIDTH = 1.5;
const PRIMARY_STROKE_SELECTED = "#ffeb3b";
const REGION_CORNER_RADIUS = 3;
const LABEL_PADDING_X = 4;
const LABEL_PADDING_Y = 3;
const REGION_INNER_PADDING = 2;
const MIN_MULTILINE_REGION_WIDTH = 120;
const MIN_MULTILINE_REGION_HEIGHT = 40;
const EVENT_INDEX_WIDTH = 22;
const EVENT_INDEX_GAP = 4;
const RESIZE_HANDLE_WIDTH = 6;
const MIN_DURATION_SEC = 0.05;
// Pixel distance within which a dragged edge snaps to a marker or region edge.
const SNAP_TOLERANCE_PX = 6;
// Minimum region width (in px) at which we render both resize handles. Below
// this width the two handles would overlap and cover the region body, so we
// suppress them entirely.
const MIN_REGION_WIDTH_FOR_HANDLES = RESIZE_HANDLE_WIDTH * 2 + 4;
const WAVEFORM_BAND_RATIO = 0.55;
const DEFAULT_DARK_REGION_LABEL = "#ffffff";
const WAVEFORM_OPACITY = 0.85;
// Backfill at most a small number of legacy AD events per visible frame to
// avoid spawning a fetch storm when scrolling/zooming through a long project.
const MAX_BACKFILL_PER_TICK = 4;
const backfillInFlight = new Set();

let eventGroup = $derived(eventGroupState[groupIndex]);
let palette = $derived(getTimelinePalette(themeState.effective));

let sortedEvents = $derived.by(() => {
    const events = eventGroup?.events || [];
    const sortable = [];
    for (let i = 0; i < events.length; i += 1) {
        const ev = events[i];
        if (ev && ev.start !== false && ev.end !== false && Number.isFinite(ev.start)) {
            sortable.push({ event: ev, originalIndex: i });
        }
    }
    sortable.sort((a, b) => a.event.start - b.event.start);
    return sortable;
});

let maxEventDurationSec = $derived(Math.max(1, eventGroup?.maxDuration || 12));

let isAudioDescription = $derived(eventGroup?.type === "audio description");

let visibleRegions = $derived.by(() => {
    if (!viewportState.containerWidth || !viewportState.pxPerSec) return [];
    const sorted = sortedEvents;
    if (sorted.length === 0) return [];

    const { scrollX, containerWidth, pxPerSec } = viewportState;
    const visStartSec = scrollX / pxPerSec;
    const visEndSec = (scrollX + containerWidth) / pxPerSec;
    const lowerSearch = visStartSec - maxEventDurationSec;
    const lo = lowerBound(sorted, lowerSearch, (e) => e.event.start);
    const hi = upperBound(sorted, visEndSec, (e) => e.event.start);

    const selected = isPrimary && Array.isArray(eventGroup?.selected) ? eventGroup.selected : [];
    const selectedSet = new Set(selected);
    const adGroup = isAudioDescription;
    const p = getTimelinePalette(themeState.effective);
    const contentHeight = Math.max(0, height - REGION_INNER_PADDING * 2);
    const waveformHeight = Math.max(8, Math.round(contentHeight * WAVEFORM_BAND_RATIO));

    const out = [];
    for (let idx = lo; idx < hi; idx += 1) {
        const item = sorted[idx];
        const ev = item.event;
        if (ev.end <= visStartSec) continue;
        const x = Math.round(ev.start * pxPerSec - scrollX);
        const widthPx = Math.max(2, Math.round((ev.end - ev.start) * pxPerSec));
        const isSelected = selectedSet.has(item.originalIndex);
        const isError = isPrimary ? hasMetricError(ev, eventGroup) : false;
        const color = isPrimary ? PRIMARY_COLOR[isSelected][isError] : SECONDARY_COLOR;
        const badges = isPrimary ? badgesForEvent(ev) : [];
        const text = regionDisplayText(ev.text);
        const label = text
            ? `${item.originalIndex + 1}  ${text.replace(/\n/g, " \u00b7 ")}`
            : String(item.originalIndex + 1);
        const badgeHandleInset = isPrimary && badges.length > 0 ? RESIZE_HANDLE_WIDTH : 0;
        const badgeReservedWidth = badges.length > 0
            ? badges.length * (BADGE_SIZE + BADGE_GAP) + LABEL_PADDING_X + badgeHandleInset
            : 0;
        const textHeight = Math.max(0, height - REGION_INNER_PADDING * 2 - LABEL_PADDING_Y * 2);
        const compactTextWidth = Math.max(0, widthPx - LABEL_PADDING_X * 2 - badgeReservedWidth);
        const multilineTextWidth = Math.max(
            0,
            widthPx - LABEL_PADDING_X * 2 - EVENT_INDEX_WIDTH - EVENT_INDEX_GAP - badgeReservedWidth
        );
        const useMultiline =
            widthPx >= MIN_MULTILINE_REGION_WIDTH &&
            height - REGION_INNER_PADDING * 2 >= MIN_MULTILINE_REGION_HEIGHT &&
            multilineTextWidth > 0;

        let waveformCanvas = null;
        let waveformWidthPx = 0;
        let waveformY = 0;
        let needsBackfill = false;
        if (adGroup) {
            const audioDur = Number(ev.audioFileDuration) || 0;
            const peaksChannel = Array.isArray(ev.peaksData) && ev.peaksData.length > 0
                ? ev.peaksData
                : null;
            if (peaksChannel && audioDur > 0) {
                waveformWidthPx = Math.max(1, Math.round(audioDur * pxPerSec));
                waveformCanvas = renderWaveformTile({
                    peaks: [peaksChannel],
                    pxPerSec,
                    durationSec: audioDur,
                    startSec: 0,
                    endSec: audioDur,
                    height: waveformHeight,
                    waveColor: p.regionWaveform
                });
                waveformY = Math.round((contentHeight - waveformHeight) / 2);
            } else if (ev.audioFile) {
                needsBackfill = true;
            }
        }

        out.push({
            id: ev.id,
            originalIndex: item.originalIndex,
            x,
            width: widthPx,
            color,
            isSelected,
            label,
            text,
            indexLabel: String(item.originalIndex + 1),
            alignment: normalizeAlignment(ev.alignment),
            useMultiline,
            compactTextWidth,
            multilineTextWidth,
            textHeight,
            badges,
            badgeHandleInset,
            waveformCanvas,
            waveformWidthPx,
            waveformHeight,
            waveformY,
            needsBackfill
        });
    }
    return out;
});

// Lazy backfill: legacy/imported AD events have `audioFile` but no `peaksData`.
// As soon as such a region becomes visible, request peak extraction and let the
// reactive store update render the waveform. Bounded per tick and de-duped via
// `backfillInFlight` so scrolling never spawns a flood of fetches.
$effect(() => {
    if (!isAudioDescription) return;
    const regions = visibleRegions;
    untrack(() => {
        const events = eventGroup?.events;
        if (!Array.isArray(events)) return;
        let started = 0;
        for (const region of regions) {
            if (started >= MAX_BACKFILL_PER_TICK) break;
            if (!region.needsBackfill) continue;
            if (backfillInFlight.has(region.id)) continue;
            const target = events[region.originalIndex];
            if (!target?.audioFile) continue;
            backfillInFlight.add(region.id);
            started += 1;
            ensureEventPeaks(target)
                .finally(() => backfillInFlight.delete(region.id));
        }
    });
});

function normalizeAlignment(alignment) {
    return alignment === "center" || alignment === "right" ? alignment : "left";
}

/**
 * Plain text for region labels. Empty Quill content can yield the literal
 * string "<br>" from convertToPlainText; treat that as no text for display.
 * @param {string} rawText
 * @returns {string}
 */
function regionDisplayText(rawText) {
    const text = convertToPlainText(rawText);
    if (text.trim().toLowerCase() === "<br>") return "";
    return text;
}

function hasMetricError(event, group) {
    if (!event || !group) return false;
    const duration = (event.end || 0) - (event.start || 0);
    if (duration <= 0) return false;
    const plainText = convertToPlainText(event.text || "");
    const lines = plainText.split("\n");
    const charsPerLine = lines.map((line) => line.length);
    const totalChars = charsPerLine.reduce((acc, n) => acc + n, 0);
    const wordCount = plainText.split(/\s+/).filter(Boolean).length;
    const cps = totalChars / duration;
    const wpm = (wordCount / duration) * 60;
    if (group.maxCps && cps > group.maxCps) return true;
    if (group.maxWpm && wpm > group.maxWpm) return true;
    if (group.maxDuration && duration > group.maxDuration) return true;
    if (group.minDuration && duration < group.minDuration) return true;
    if (group.maxChars && charsPerLine.some((c) => c > group.maxChars)) return true;
    if (group.maxLines && lines.length > group.maxLines) return true;
    return false;
}

function handleClick(region, evt) {
    if (!isPrimary) return;
    onRegionClick?.(region.originalIndex, evt);
}

function handleDoubleClick(region, evt) {
    if (!isPrimary) {
        if (window.player) {
            const startTime = eventGroup?.events?.[region.originalIndex]?.start;
            if (Number.isFinite(startTime)) window.player.currentTime = startTime;
        }
        return;
    }
    onRegionDoubleClick?.(region.originalIndex, evt);
}

const labelFontSize = $derived(Number(editorState.timelineFontSize) || 12);
const labelFill = $derived.by(() => {
    const custom = editorState.timelineFontColor;
    if (
        themeState.effective === "light" &&
        (!custom || custom.toLowerCase() === DEFAULT_DARK_REGION_LABEL)
    ) {
        return palette.regionLabel;
    }
    return custom || DEFAULT_DARK_REGION_LABEL;
});

// When overlap protection is off (the "Allow Overlap" toolbar toggle), drags
// are no longer clamped at neighbor edges; instead the intruded-upon neighbor
// is rippled to preserve the frame gap. Defaults to protected when unset.
function overlapProtectionOn() {
    return editorState.timelineOverlapProtection !== false;
}

// ===== Drag (whole-region move) =====
let activeMoveDrag = null;    // { eventId, originalIndex, startSec, endSec, movingIndexes, initialAbsY, allowOverlap, leftRippleIndex, rightRippleIndex }

function startMoveDrag(region, e) {
    if (!isPrimary) return;
    // Konva fires `dragstart` on the dragged node and then bubbles it up
    // through the tree. When the user starts dragging a resize handle, the
    // bubble reaches THIS parent Group's `ondragstart` even though the Group
    // itself is not being dragged (Konva's `_listenDrag` correctly skips
    // creating a drag-element for the Group because the handle is already in
    // `_dragElements`). Without this guard we would mistakenly populate
    // `activeMoveDrag` and then commit a garbage delta in `endMoveDrag`
    // (reading `node.x()` off the handle), which is what caused both start
    // AND end to shift when only a single handle was dragged.
    if (e && e.target && e.currentTarget && e.target !== e.currentTarget) return;
    const group = eventGroup;
    if (!group) return;
    const me = group.events[region.originalIndex];
    if (!me) return;
    const sel = Array.isArray(group.selected) ? group.selected : [];
    const movingIndexes = new Set(sel.includes(region.originalIndex) ? sel : [region.originalIndex]);
    const allowOverlap = !overlapProtectionOn();
    // Cache the neighbors to ripple, keyed off the outermost edges of the moving
    // cluster. Only the side matching the drag direction ends up overlapping.
    let leftRippleIndex = -1;
    let rightRippleIndex = -1;
    if (allowOverlap) {
        let leftIdx = -1;
        let rightIdx = -1;
        let minStart = Infinity;
        let maxEnd = -Infinity;
        for (const idx of movingIndexes) {
            const ev = group.events[idx];
            if (!ev) continue;
            if (Number.isFinite(ev.start) && ev.start < minStart) { minStart = ev.start; leftIdx = idx; }
            if (Number.isFinite(ev.end) && ev.end > maxEnd) { maxEnd = ev.end; rightIdx = idx; }
        }
        if (leftIdx >= 0) leftRippleIndex = findRippleTargetIndex(group.events, leftIdx, "left", movingIndexes);
        if (rightIdx >= 0) rightRippleIndex = findRippleTargetIndex(group.events, rightIdx, "right", movingIndexes);
    }
    activeMoveDrag = {
        eventId: region.id,
        originalIndex: region.originalIndex,
        startSec: me.start,
        endSec: me.end,
        movingIndexes,
        allowOverlap,
        leftRippleIndex,
        rightRippleIndex,
        // Capture the row's absolute Y so the bound func can lock the drag
        // to the X axis (otherwise the region can be dragged off-track).
        initialAbsY: e?.target?.getAbsolutePosition?.()?.y ?? 0
    };
}

function moveDragBoundFunc(pos, region) {
    const pxPerSec = viewportState.pxPerSec;
    if (!pxPerSec || !activeMoveDrag) return { x: pos.x, y: pos.y };

    const events = eventGroup?.events || [];
    const movingIndexes = activeMoveDrag.movingIndexes;
    const frameGapSec = frameGapToSeconds(editorState.minFrameGap || 0, projectState.frameRate || 29.97);

    // pos.x is the absolute X of the dragged Group's top-left.
    const desiredStart = (pos.x + viewportState.scrollX) / pxPerSec;
    const desiredDelta = desiredStart - activeMoveDrag.startSec;

    // Snap either the START or the END edge -- whichever lands closest to a
    // target -- but only accept a snap whose required delta survives the
    // neighbor clamp. Otherwise we'd "snap" to a target the region can't
    // actually reach and the clamp would silently undo it (feeling broken).
    // Markers and region edges both use a 6px window.
    // When overlap is allowed the neighbor clamp is skipped entirely (the
    // neighbor is rippled on commit instead), so any snap target is accepted.
    const useClamp = !activeMoveDrag.allowOverlap;
    const { markers, edges } = buildSnapTargets(movingIndexes);
    const movedStart = activeMoveDrag.startSec + desiredDelta;
    const movedEnd = activeMoveDrag.endSec + desiredDelta;
    let bestDelta = desiredDelta;
    let bestAdj = Infinity;
    for (const edge of [movedStart, movedEnd]) {
        const snap = nearestSnapTarget({
            time: edge,
            markers,
            edges,
            pxPerSec,
            edgeTolerancePx: SNAP_TOLERANCE_PX,
            markerTolerancePx: SNAP_TOLERANCE_PX
        });
        if (!snap) continue;
        const adj = snap.target - edge;
        if (Math.abs(adj) >= bestAdj) continue;
        const candidateDelta = desiredDelta + adj;
        if (useClamp) {
            const clamped = clampUniformDelta({ events, movingIndexes, delta: candidateDelta, frameGapSec });
            if (Math.abs(clamped - candidateDelta) >= 1e-6) continue;
        }
        bestAdj = Math.abs(adj);
        bestDelta = candidateDelta;
    }

    const finalDelta = useClamp
        ? clampUniformDelta({ events, movingIndexes, delta: bestDelta, frameGapSec })
        : bestDelta;
    const newStart = activeMoveDrag.startSec + finalDelta;
    const newX = Math.round(newStart * pxPerSec - viewportState.scrollX);
    // Lock Y to the row's initial absolute position so dragging is X-only.
    return { x: newX, y: activeMoveDrag.initialAbsY };
}

function endMoveDrag(region, e) {
    if (!isPrimary || !activeMoveDrag) return;
    // Defensive twin of the check in `startMoveDrag`: if `dragend` bubbled
    // up from a child (resize handle) and somehow `activeMoveDrag` was set,
    // bail out before committing a move. (Normally the startMoveDrag guard
    // already kept `activeMoveDrag` null in this case.)
    if (e && e.target && e.currentTarget && e.target !== e.currentTarget) {
        activeMoveDrag = null;
        return;
    }
    const node = e?.target;
    const pxPerSec = viewportState.pxPerSec;
    if (!node || !pxPerSec) {
        activeMoveDrag = null;
        return;
    }
    const newX = node.x();
    const newStart = (newX + viewportState.scrollX) / pxPerSec;
    const delta = newStart - activeMoveDrag.startSec;
    const events = eventGroup.events;
    const frameGapSec = frameGapToSeconds(editorState.minFrameGap || 0, projectState.frameRate || 29.97);

    if (activeMoveDrag.allowOverlap) {
        if (Math.abs(delta) > 1e-4) {
            applyMoveRipple({
                events,
                movingIndexes: activeMoveDrag.movingIndexes,
                delta,
                leftRippleIndex: activeMoveDrag.leftRippleIndex,
                rightRippleIndex: activeMoveDrag.rightRippleIndex,
                frameGapSec,
                minDurationSec: MIN_DURATION_SEC
            });
            onDragEndCommit?.();
        }
    } else {
        const finalDelta = clampUniformDelta({
            events,
            movingIndexes: activeMoveDrag.movingIndexes,
            delta,
            frameGapSec
        });

        if (Math.abs(finalDelta) > 1e-4) {
            for (const idx of activeMoveDrag.movingIndexes) {
                const ev = events[idx];
                if (!ev) continue;
                ev.start = ev.start + finalDelta;
                ev.end = ev.end + finalDelta;
            }
            onDragEndCommit?.();
        }
    }
    // Snap node back to its data-driven x to avoid a one-frame visual jump
    // before Svelte runs the next render.
    const finalX = Math.round((events[region.originalIndex].start) * pxPerSec - viewportState.scrollX);
    node.x(finalX);
    activeMoveDrag = null;
}

// ===== Resize (per-region edge handles) =====
// $state so the template's handle-visibility {#if} can react: we keep the
// handle of the actively-resizing region rendered even if its width drops
// below MIN_REGION_WIDTH_FOR_HANDLES, otherwise the {#if} would destroy the
// Konva node mid-drag and silently abort the drag (no dragend would fire and
// activeResizeDrag would leak).
let activeResizeDrag = $state(null); // { eventId, originalIndex, side, startSec, endSec, bounds, resolvedTime, initialAbsY }

// Build snap targets for the current settings, keeping markers and region
// edges separate so each can use its own tolerance (both pixel-based).
// Markers come from the selected marker list (if snapToShotChanges); edges
// come from other events (if snapToRegions). `movingIndexes` are excluded so
// a region never snaps to its own edge.
function buildSnapTargets(movingIndexes) {
    const markers = [];
    const edges = [];
    if (editorState.snapToShotChanges && markerState) {
        const list = markerState.lists?.[markerState.selected];
        const markerList = Array.isArray(list?.markers) ? list.markers : [];
        for (const mk of markerList) {
            const t = Number(mk.time);
            if (Number.isFinite(t)) markers.push(t);
        }
    }
    if (editorState.snapToRegions) {
        const events = eventGroup?.events || [];
        for (const t of gatherRegionEdgeTimes({ events, movingIndexes })) edges.push(t);
    }
    return { markers, edges };
}

function getMediaDuration() {
    const d = Number(playerState.duration);
    return Number.isFinite(d) && d > 0 ? d : Infinity;
}

function startResizeDrag(region, side, e) {
    const events = eventGroup?.events;
    if (!events) return;
    const me = events[region.originalIndex];
    if (!me) return;
    // Cache the hard bounds ONCE at drag start. `computeHandleBounds` derives
    // the limit from every OTHER event's edge; those don't move during the
    // drag (only this region's edge does), so the bounds stay valid for the
    // whole gesture. Freezing here also avoids the dragged edge re-deriving a
    // looser limit off its own live-updated position.
    const frameGapSec = frameGapToSeconds(editorState.minFrameGap || 0, projectState.frameRate || 29.97);
    const allowOverlap = !overlapProtectionOn();
    // With overlap allowed the handle can move past neighbors (bounds ignore
    // them); the intruded neighbor is rippled live in `onResizeDragMove`.
    const bounds = allowOverlap
        ? computePermissiveHandleBounds({
            events,
            eventIndex: region.originalIndex,
            side,
            minDurationSec: MIN_DURATION_SEC,
            duration: getMediaDuration()
        })
        : computeHandleBounds({
            events,
            eventIndex: region.originalIndex,
            side,
            frameGapSec,
            minDurationSec: MIN_DURATION_SEC,
            duration: getMediaDuration()
        });
    activeResizeDrag = {
        eventId: region.id,
        originalIndex: region.originalIndex,
        side,
        startSec: me.start,
        endSec: me.end,
        bounds,
        allowOverlap,
        frameGapSec,
        rippleIndex: allowOverlap ? findRippleTargetIndex(events, region.originalIndex, side) : -1,
        resolvedTime: side === "left" ? me.start : me.end,
        // Capture the handle's initial absolute Y so the bound func can lock
        // the drag to the X axis.
        initialAbsY: e?.target?.getAbsolutePosition?.()?.y ?? 0
    };
}

function leftHandleBoundFunc(pos, region) {
    const pxPerSec = viewportState.pxPerSec;
    if (!pxPerSec || !activeResizeDrag) return pos;
    const movingIndexes = new Set([activeResizeDrag.originalIndex]);

    // pos.x is the absolute X of the handle's top-left; the side-aligned left
    // handle uses that same edge as the event start.
    const desiredStart = (pos.x + viewportState.scrollX) / pxPerSec;
    // `resolveHandleTime` snaps only to targets inside the cached bounds and
    // then hard-clamps, so the edge can never cross a neighbor (boundaries hold
    // even when snapping is off, because the clamp runs regardless). Markers
    // and region edges both use a 6px window.
    const { markers, edges } = buildSnapTargets(movingIndexes);
    const newStart = resolveHandleTime({
        desiredTime: desiredStart,
        bounds: activeResizeDrag.bounds,
        markers,
        edges,
        pxPerSec,
        edgeTolerancePx: SNAP_TOLERANCE_PX,
        markerTolerancePx: SNAP_TOLERANCE_PX
    });
    activeResizeDrag.resolvedTime = newStart;
    const newHandleX = Math.round(newStart * pxPerSec - viewportState.scrollX);
    return { x: newHandleX, y: activeResizeDrag.initialAbsY };
}

function rightHandleBoundFunc(pos, region) {
    const pxPerSec = viewportState.pxPerSec;
    if (!pxPerSec || !activeResizeDrag) return pos;
    const movingIndexes = new Set([activeResizeDrag.originalIndex]);

    // The side-aligned right handle's right edge represents the event end.
    const handleRightAbsX = pos.x + RESIZE_HANDLE_WIDTH;
    const desiredEnd = (handleRightAbsX + viewportState.scrollX) / pxPerSec;
    const { markers, edges } = buildSnapTargets(movingIndexes);
    const newEnd = resolveHandleTime({
        desiredTime: desiredEnd,
        bounds: activeResizeDrag.bounds,
        markers,
        edges,
        pxPerSec,
        edgeTolerancePx: SNAP_TOLERANCE_PX,
        markerTolerancePx: SNAP_TOLERANCE_PX
    });
    activeResizeDrag.resolvedTime = newEnd;
    const newHandleX = Math.round(newEnd * pxPerSec - viewportState.scrollX - RESIZE_HANDLE_WIDTH);
    return { x: newHandleX, y: activeResizeDrag.initialAbsY };
}

function pinResizeHandleNode(node, side, ev) {
    const pxPerSec = viewportState.pxPerSec;
    if (!node || !pxPerSec || !ev) return;
    const widthPx = Math.max(2, Math.round((ev.end - ev.start) * pxPerSec));
    node.y(0);
    node.x(side === "left" ? 0 : widthPx - RESIZE_HANDLE_WIDTH);
}

// Live commit during the drag: read the handle's clamped absolute position
// back, convert to a timeline time, and update ev.start/ev.end immediately.
// This is what keeps the region body, label, badges, opposite handle, and
// parent Group x in sync with the handle while the user is still dragging
// (otherwise the handle visually "slides away" from the static body and
// times only update on dragend).
function onResizeDragMove(region, side, e) {
    if (!activeResizeDrag) return;
    const node = e?.target;
    const pxPerSec = viewportState.pxPerSec;
    const events = eventGroup?.events;
    if (!node || !pxPerSec || !events) return;
    const ev = events[activeResizeDrag.originalIndex];
    if (!ev) return;
    // Trust the time resolved by the bound func. Reading the handle's absolute
    // position after ev.start changes can double-count the parent Group shift
    // during a left-edge resize, which is what can make snapping jump the start
    // handle toward markers near the end edge.
    const handleAbsX = node.getAbsolutePosition().x;
    const fallbackEdgeAbsX = side === "left" ? handleAbsX : handleAbsX + RESIZE_HANDLE_WIDTH;
    const fallbackTime = (fallbackEdgeAbsX + viewportState.scrollX) / pxPerSec;
    const newTime = Number.isFinite(activeResizeDrag.resolvedTime)
        ? activeResizeDrag.resolvedTime
        : fallbackTime;
    if (activeResizeDrag.allowOverlap) {
        // Move the dragged edge freely (within permissive bounds) and ripple the
        // cached neighbor so the frame gap is preserved instead of blocking.
        applyResizeRipple({
            events,
            eventIndex: activeResizeDrag.originalIndex,
            side,
            newTime,
            leftRippleIndex: side === "left" ? activeResizeDrag.rippleIndex : -1,
            rightRippleIndex: side === "right" ? activeResizeDrag.rippleIndex : -1,
            frameGapSec: activeResizeDrag.frameGapSec,
            minDurationSec: MIN_DURATION_SEC,
            duration: getMediaDuration()
        });
        activeResizeDrag.resolvedTime = side === "left" ? ev.start : ev.end;
        pinResizeHandleNode(node, side, ev);
        return;
    }
    // Clamp against the SAME cached bounds the bound func used. This makes the
    // store the authoritative gate: even if the reactive handle-x binding
    // desyncs from the bound-func position mid-drag, the committed time can
    // never cross a neighbor boundary or break min duration.
    const { min, max } = activeResizeDrag.bounds;
    const clamped = Math.min(Math.max(newTime, min), max);
    if (side === "left") {
        if (Math.abs(ev.start - clamped) > 1e-6) ev.start = clamped;
    } else {
        if (Math.abs(ev.end - clamped) > 1e-6) ev.end = clamped;
    }
    activeResizeDrag.resolvedTime = clamped;
    pinResizeHandleNode(node, side, ev);
}

function endResizeDrag(region, side, e) {
    if (!activeResizeDrag) return;
    // Live updates during `ondragmove` have already committed the final
    // ev.start/ev.end. Re-run once for the very last pointer position (in
    // case Konva fired dragend without a final dragmove).
    onResizeDragMove(region, side, e);

    // Re-pin the dragged handle to its data-driven LOCAL position. The LEFT
    // handle's x prop is a constant (0), so svelte-konva never
    // re-applies it after Konva's drag moved the node AND the region Group
    // reactively shifted (because ev.start changed during the drag). Without
    // this the handle is left at its drag-final offset and visually overlaps
    // the region body. Resetting the local x (relative to the now-repositioned
    // Group) realigns it; we do both sides for safety.
    pinResizeHandleNode(e?.target, side, eventGroup?.events?.[activeResizeDrag.originalIndex]);
    activeResizeDrag = null;
    // Konva may have captured the pointer during the drag, so a normal
    // pointerleave on the handle can be skipped; reset the cursor explicitly.
    setStageCursor(e, "");
    onDragEndCommit?.();
}

// Resize handles are Konva canvas shapes, so there is no DOM element to style
// with CSS. Set the cursor on the stage's container element instead so hovering
// a handle signals the user can drag to resize the event's start/end times.
function setStageCursor(e, cursor) {
    const container = e?.target?.getStage?.()?.container?.();
    if (container) container.style.cursor = cursor;
}

function handleBackgroundPointerDown(e) {
    if (!onBackgroundPointerDown) return;
    const pxPerSec = viewportState.pxPerSec;
    if (!pxPerSec) return;
    // Konva.Stage pointer position is in stage coords (handles devicePixelRatio).
    const stage = e?.target?.getStage?.();
    const pointer = stage?.getPointerPosition?.();
    if (!pointer) return;
    const time = (pointer.x + viewportState.scrollX) / pxPerSec;
    onBackgroundPointerDown(time, e?.evt);
}
</script>

<Group y={y}>
    <Rect
        x={0}
        y={0}
        width={viewportState.containerWidth}
        height={height}
        fill={isPrimary ? palette.trackRowBg : palette.trackRowBgAlt}
        listening={Boolean(onBackgroundPointerDown)}
        onpointerdown={handleBackgroundPointerDown}
        staticConfig={true} />

    {#each visibleRegions as region (region.id)}
        <Group
            x={region.x}
            y={REGION_INNER_PADDING}
            draggable={isPrimary}
            dragBoundFunc={(pos) => moveDragBoundFunc(pos, region)}
            onpointerclick={(e) => handleClick(region, e?.evt)}
            onpointerdblclick={(e) => handleDoubleClick(region, e?.evt)}
            ondragstart={(e) => startMoveDrag(region, e)}
            ondragend={(e) => endMoveDrag(region, e)}>
            <Rect
                width={region.width}
                height={height - REGION_INNER_PADDING * 2}
                fill={region.color}
                stroke={region.isSelected ? PRIMARY_STROKE_SELECTED : isPrimary ? "transparent" : SECONDARY_STROKE}
                strokeWidth={region.isSelected ? 1.5 : isPrimary ? 0 : SECONDARY_STROKE_WIDTH}
                cornerRadius={REGION_CORNER_RADIUS} />
            {#if region.waveformCanvas}
                <Image
                    x={0}
                    y={region.waveformY}
                    width={Math.min(region.width, region.waveformWidthPx)}
                    height={region.waveformHeight}
                    image={region.waveformCanvas}
                    listening={false}
                    opacity={WAVEFORM_OPACITY} />
            {/if}
            {#if region.width > 24}
                {#if region.useMultiline}
                    <Text
                        x={LABEL_PADDING_X}
                        y={LABEL_PADDING_Y}
                        width={EVENT_INDEX_WIDTH}
                        height={region.textHeight}
                        text={region.indexLabel}
                        fontSize={labelFontSize}
                        fill={labelFill}
                        ellipsis={true}
                        wrap="none"
                        align="right"
                        listening={false} />
                    {#if region.text}
                        <Text
                            x={LABEL_PADDING_X + EVENT_INDEX_WIDTH + EVENT_INDEX_GAP}
                            y={LABEL_PADDING_Y}
                            width={region.multilineTextWidth}
                            height={region.textHeight}
                            text={region.text}
                            fontSize={labelFontSize}
                            fill={labelFill}
                            ellipsis={true}
                            wrap="word"
                            align={region.alignment}
                            listening={false} />
                    {/if}
                {:else}
                    <Text
                        x={LABEL_PADDING_X}
                        y={LABEL_PADDING_Y}
                        width={region.compactTextWidth}
                        text={region.label}
                        fontSize={labelFontSize}
                        fill={labelFill}
                        ellipsis={true}
                        wrap="none"
                        listening={false} />
                {/if}
            {/if}
            {#if region.badges.length > 0 && region.width > BADGE_SIZE * 2}
                {#each region.badges as badgeKey, i (badgeKey)}
                    {@const badge = BADGES[badgeKey]}
                    {@const bx = region.width - LABEL_PADDING_X - region.badgeHandleInset - (region.badges.length - i) * (BADGE_SIZE + BADGE_GAP) + BADGE_GAP}
                    {@const by = height - REGION_INNER_PADDING * 2 - LABEL_PADDING_Y - BADGE_SIZE}
                    <Rect
                        x={bx}
                        y={by}
                        width={BADGE_SIZE}
                        height={BADGE_SIZE}
                        fill={badge.fill}
                        cornerRadius={2}
                        listening={false} />
                    <Text
                        x={bx}
                        y={by + 1}
                        width={BADGE_SIZE}
                        height={BADGE_SIZE}
                        text={badge.label}
                        fontSize={9}
                        fontStyle="bold"
                        align="center"
                        fill={badge.text}
                        listening={false} />
                {/each}
            {/if}
            {#if isPrimary && (region.width >= MIN_REGION_WIDTH_FOR_HANDLES || activeResizeDrag?.eventId === region.id)}
                {@const handleFill = region.isSelected ? palette.handleFillSelected : palette.handleFillIdle}
                <!--
                    Resize handles are always rendered on primary-track regions
                    (not just when selected) so users can immediately see and
                    grab them. The fill dims when the region is unselected so
                    the always-on handles do not visually overpower the regions.

                    During an active resize we ALSO keep the handles for the
                    dragging region rendered even if its width drops below
                    MIN_REGION_WIDTH_FOR_HANDLES; otherwise the {#if} would
                    destroy the Konva node Konva is mid-drag on, silently
                    aborting the drag (no dragend, leaked activeResizeDrag).
                -->
                <Rect
                    x={0}
                    y={0}
                    width={RESIZE_HANDLE_WIDTH}
                    height={height - REGION_INNER_PADDING * 2}
                    fill={handleFill}
                    cornerRadius={2}
                    draggable={true}
                    dragBoundFunc={(pos) => leftHandleBoundFunc(pos, region)}
                    onpointerenter={(e) => setStageCursor(e, "col-resize")}
                    onpointerleave={(e) => setStageCursor(e, "")}
                    ondragstart={(e) => startResizeDrag(region, "left", e)}
                    ondragmove={(e) => onResizeDragMove(region, "left", e)}
                    ondragend={(e) => endResizeDrag(region, "left", e)} />
                <Rect
                    x={region.width - RESIZE_HANDLE_WIDTH}
                    y={0}
                    width={RESIZE_HANDLE_WIDTH}
                    height={height - REGION_INNER_PADDING * 2}
                    fill={handleFill}
                    cornerRadius={2}
                    draggable={true}
                    dragBoundFunc={(pos) => rightHandleBoundFunc(pos, region)}
                    onpointerenter={(e) => setStageCursor(e, "col-resize")}
                    onpointerleave={(e) => setStageCursor(e, "")}
                    ondragstart={(e) => startResizeDrag(region, "right", e)}
                    ondragmove={(e) => onResizeDragMove(region, "right", e)}
                    ondragend={(e) => endResizeDrag(region, "right", e)} />
            {/if}
        </Group>
    {/each}
</Group>
